/**
 * Advanced Filters Component
 * Componente de filtro centralizado reutilizável
 */

import React, { useState } from 'react';
import {
  Box,
  Chip,
  Button,
  Menu,
  Typography,
  Divider,
  TextField,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Close, Tune } from '@mui/icons-material';
import { ClientFilter } from './ClientFilter';

export interface FilterConfig {
  showClientFilter?: boolean;
  showDateRangeFilter?: boolean;
  showSearchFilter?: boolean;
  pageType?: string;
  variant?: 'default' | 'mobile' | 'compact';
}

interface AdvancedFiltersProps {
  config: FilterConfig;
  selectedClients?: string[];
  onClientsChange?: (clientIds: string[]) => void;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  config,
  selectedClients = [],
  onClientsChange = () => {},
  searchValue = '',
  onSearchChange = () => {}
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const activeFiltersCount = React.useMemo(() => {
    let count = 0;
    if (selectedClients.length > 0) count++;
    if (searchValue) count++;
    return count;
  }, [selectedClients, searchValue]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClearAllFilters = () => {
    onClientsChange([]);
    onSearchChange('');
  };

  const renderActiveFilters = () => {
    const chips: React.ReactNode[] = [];

    if (selectedClients.length > 0) {
      chips.push(
        <Chip
          key="clients"
          label={`${selectedClients.length} cliente${selectedClients.length > 1 ? 's' : ''}`}
          size="small"
          onDelete={() => onClientsChange([])}
          deleteIcon={<Close fontSize="small" sx={{ color: 'white !important' }} />}
          sx={{
            height: 24,
            backgroundColor: '#21316a',
            color: 'white',
            '& .MuiChip-label': { color: 'white' },
            '& .MuiChip-deleteIcon': { color: 'white !important' }
          }}
        />
      );
    }

    if (searchValue) {
      chips.push(
        <Chip
          key="search"
          label={`Busca: ${searchValue}`}
          size="small"
          onDelete={() => onSearchChange('')}
          deleteIcon={<Close fontSize="small" sx={{ color: 'white !important' }} />}
          sx={{
            height: 24,
            backgroundColor: '#21316a',
            color: 'white',
            '& .MuiChip-label': { color: 'white' },
            '& .MuiChip-deleteIcon': { color: 'white !important' }
          }}
        />
      );
    }

    return chips;
  };

  if (isMobile && config.variant === 'compact') {
    return null;
  }

  if (config.variant === 'mobile') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {config.showClientFilter !== false && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Cliente
            </Typography>
            <ClientFilter selectedClients={selectedClients} onClientsChange={onClientsChange} />
          </Box>
        )}

        {config.showSearchFilter && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Busca
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar..."
              value={searchValue}
              onChange={e => onSearchChange(e.target.value)}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Button
            size="small"
            onClick={handleClearAllFilters}
            disabled={activeFiltersCount === 0}
            color="error"
          >
            Limpar Filtros
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {activeFiltersCount > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
          {renderActiveFilters()}
        </Stack>
      )}

      <Button
        variant={activeFiltersCount > 0 ? 'contained' : 'outlined'}
        size="small"
        startIcon={<Tune />}
        onClick={handleMenuClick}
        sx={{
          minWidth: 'auto',
          height: 32,
          ...(activeFiltersCount > 0
            ? {
                backgroundColor: '#21316a',
                '&:hover': { backgroundColor: '#1a2854' },
                '&::after': {
                  content: `"${activeFiltersCount}"`,
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  backgroundColor: theme.palette.error.main,
                  color: 'white',
                  borderRadius: '50%',
                  width: 18,
                  height: 18,
                  fontSize: '0.7rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                },
                position: 'relative'
              }
            : {
                // Quando não há filtros ativos: borda e texto escuros (header claro)
                borderColor: '#334155',
                color: '#334155',
                '& .MuiSvgIcon-root': {
                  color: 'inherit'
                },
                '&:hover': {
                  borderColor: '#21316a',
                  backgroundColor: 'rgba(33, 49, 106, 0.06)',
                  color: '#21316a'
                }
              })
        }}
      >
        Filtros
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 300, maxWidth: 400, mt: 1 } }}
      >
        {config.showClientFilter !== false && (
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Cliente
            </Typography>
            <ClientFilter selectedClients={selectedClients} onClientsChange={onClientsChange} />
          </Box>
        )}

        {config.showSearchFilter && (
          <>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Busca
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar..."
                value={searchValue}
                onChange={e => onSearchChange(e.target.value)}
              />
            </Box>
          </>
        )}

        <Divider />
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            size="small"
            onClick={handleClearAllFilters}
            disabled={activeFiltersCount === 0}
            color="error"
          >
            Limpar Filtros
          </Button>
          <Button size="small" onClick={handleMenuClose}>
            Fechar
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default AdvancedFilters;
